import React, { FC, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ReactResizeDetector from 'react-resize-detector';
import { local } from 'store2';

import { Banner, Envelope, Typography } from '@dev/ee-components';
import { safelyDecodeBase64Url } from '@dev/ee-tools';
import styled from 'styled-components';
import { AccessLevel, CampaignType } from '@dev/ee-api/ee-api-models';
import { find } from 'lodash-es';

import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { useParams } from 'react-router-dom';
import { ApplicationState } from '@/store';
import useMobile from '@/services/hooks/useMobile';

import { CampaignReportOverviewProps } from './CampaignReportOverviewInterface';
import { CreateListButton } from './components/CreateListButton';
import { isClickTrackingEnabled, isOpenTrackingEnabled, isLinkClickTrackingEnabled } from './helperFunctions';
import { useComponentDidMountOrWillUnmount, useComponentDidUpdate } from './hooks';
import { Chart } from './components/LineChart';
import { PerformanceStats } from './components/PerformanceStats';
import { TriggerText } from './components/TriggerText';
import { OverviewStats } from './components/OverviewStats';
import { ChartTitle } from './components/ChartTitle';
import { TableWithLinks } from './components/TableWithLinks';
import { SendingCampaignBanner } from './components/SendingCampaignBanner';
import { StyledProps } from '../../CampaignsInterface';

dayjs.extend(duration);

const OverviewWrapper = styled.div<StyledProps>`
    padding-top: 32px;
    width: ${({ isMobile }): string => (isMobile ? '100%' : '99%')};
`;

const PerformanceRowContainer = styled.div<StyledProps>`
    ${({ isMobile }): string =>
        !isMobile &&
        `
    display: flex;
    justify-content: space-between;
    flex-direction: row;
    `}
`;

export const ResponsiveResizeWrapper = styled.div<StyledProps>`
    max-width: ${({ width }): string => width};
    width: 100%;
`;

export const ContentLoaderWrapper = styled.div`
    margin-right: 16px;
    min-width: 45px;
    flex: 1;
`;

const OpenRateBannerWrapper = styled.div`
    padding: 32px 0px;
`;

const CampaignReportOverview: FC<CampaignReportOverviewProps> = (): JSX.Element => {
    const { t } = useTranslation();
    const isMobile = useMobile();
    const { name, type } = useParams<{ name: string; type: CampaignType }>();

    const { campaigns, reports } = useSelector((state: ApplicationState) => state);
    const { pickedCampaign } = campaigns;
    const { AdvancedOptions, GeneralInfo } = useSelector((state: ApplicationState) => state.account);

    useComponentDidMountOrWillUnmount();
    useComponentDidUpdate();

    const isSendingCampaign = campaigns?.pickedCampaign?.campaign?.Status === 'Sending';
    const isClicksTracked = isClickTrackingEnabled(pickedCampaign?.campaign, AdvancedOptions);
    const isOpensTracked = isOpenTrackingEnabled(pickedCampaign?.campaign, AdvancedOptions);
    const isLinkTracked = isLinkClickTrackingEnabled(AdvancedOptions);
    const isFromAutomation = pickedCampaign?.campaign?.FromAutomation;
    const decodedName = safelyDecodeBase64Url(name);
    const openRateStorage = JSON.parse(local.get('showOpenRateBanner')) || [];

    const haveAnySortOfTrackingEnabled = isClicksTracked || isOpensTracked;

    const [isBannerHidden, setToggleBanner] = useState(() => {
        if (!openRateStorage) {
            return false;
        }
        return !!find(openRateStorage, (el) => el === name);
    });

    const showBannerAfter24Hours = (): boolean => {
        let isOpenRateBanner = false;

        // displays the banner after 24 hours from sending the campaign
        if (campaigns?.pickedCampaign?.campaign?.TriggerOptions?.DateToSendOn) {
            const currentDate = dayjs().toISOString();
            const sendDate = campaigns.pickedCampaign.campaign.TriggerOptions.DateToSendOn;
            const calc = dayjs(currentDate).diff(sendDate, 'hour');

            isOpenRateBanner = calc >= 24;
        }

        return isOpenRateBanner && isOpensTracked;
    };
    const accountAl = GeneralInfo?.AccountAccessLevel;

    return (
        <ReactResizeDetector handleWidth handleHeight>
            {({ width }) => (
                <OverviewWrapper isMobile={isMobile}>
                    <ResponsiveResizeWrapper width={`${width}px`}>
                        <OverviewStats campaigns={campaigns} name={name} type={type} />
                        {pickedCampaign?.campaign?.Type === CampaignType.Automated && (
                            <TriggerText pickedCampaign={pickedCampaign} />
                        )}
                        <PerformanceRowContainer isMobile={isMobile}>
                            <Typography variant="h1">{t('performance')}</Typography>
                            {accountAl?.includes?.(AccessLevel.ModifyContacts) && haveAnySortOfTrackingEnabled && (
                                <CreateListButton
                                    campaign={campaigns?.pickedCampaign?.campaign}
                                    decodedName={decodedName}
                                />
                            )}
                        </PerformanceRowContainer>
                        {isSendingCampaign && !isFromAutomation && <SendingCampaignBanner name={name} type={type} />}
                        <PerformanceStats
                            campaigns={campaigns}
                            isClicksTracked={isClicksTracked}
                            isOpensTracked={isOpensTracked}
                        />
                        {(isClicksTracked || isOpensTracked) && <ChartTitle pickedCampaign={pickedCampaign} />}
                        {(isClicksTracked || isOpensTracked) && (
                            <Chart
                                type={type}
                                AdvancedOptions={AdvancedOptions}
                                isClicksTracked={isClicksTracked}
                                isOpensTracked={isOpensTracked}
                                reports={reports}
                                isLinkTracked={isLinkTracked}
                                campaigns={campaigns}
                            />
                        )}
                        {showBannerAfter24Hours() && !isBannerHidden && (
                            <OpenRateBannerWrapper>
                                <Banner
                                    title={t('openratethrough')}
                                    body={<div>{t('createlistbtn_desc')}</div>}
                                    isInfo={true}
                                    imageToDisplay={<Envelope />}
                                    onClose={(): void => {
                                        openRateStorage.push(name);
                                        local.set('showOpenRateBanner', JSON.stringify(openRateStorage));
                                        setToggleBanner(true);
                                    }}
                                    isMobile={isMobile}
                                />
                            </OpenRateBannerWrapper>
                        )}
                        {isClicksTracked && (
                            <div style={{ paddingTop: showBannerAfter24Hours() ? '0px' : '31px' }}>
                                <TableWithLinks name={name} type={type} reports={reports} />
                            </div>
                        )}
                    </ResponsiveResizeWrapper>
                </OverviewWrapper>
            )}
        </ReactResizeDetector>
    );
};

export default CampaignReportOverview;
